import { useSubscription, useApolloClient } from '@apollo/client';
import { CommoditiesUpdatedPayload, CrewMember, PersonaType } from '@bechtel/types';
import { useEffect } from 'react';
import { SUBSCRIPTION_COMMODITIES_UPDATED } from '@bechtel/client-gql';
import {
  useAppUser,
  CacheKeys,
  useEnvProvider,
  useCommodityCache,
  ProjectAppTypes,
  useConfig,
} from '@bechtel/client-common';
import { includes } from 'lodash';
import { useLocation } from 'react-router-dom';
export function useCommoditySubscription() {
  const { features } = useConfig();
  const { data } = useSubscription<{ commoditiesUpdated: CommoditiesUpdatedPayload }>(SUBSCRIPTION_COMMODITIES_UPDATED);
  const { updateCommoditiesCache } = useCommodityCache();
  const { user } = useAppUser();
  const client = useApolloClient();
  const { envConfig } = useEnvProvider();
  const location = useLocation();
  const dashboardSelectedUser: CrewMember = JSON.parse(sessionStorage.getItem(CacheKeys.DashboardSelectedUser));
  const corePersonaTypes = [PersonaType.SUPERINTENDENT, PersonaType.GENERAL_FOREMAN, PersonaType.FOREMAN];
  const isASAppType = envConfig.appName === ProjectAppTypes.AS;
  useEffect(() => {
    if (
      !data ||
      !data.commoditiesUpdated ||
      !data.commoditiesUpdated.commodityIds ||
      data.commoditiesUpdated.commodityIds.length == 0
    )
      return;
    updateCommoditiesCache(data.commoditiesUpdated.commodityIds);
  }, [data]);

  useEffect(() => {
    const pathName = location.pathname;
    const fetchDashboardStatForSuperGfForeman = async () => {
      await client.refetchQueries({
        include: [
          'commodities',
          'commodityStats',
          'commodityStatsPriority',
          'commodityWeeklyStats',
          'crewProgressStats',
          'commodityDashboardTabStats',
        ],
      });
      if (features?.showMapView) {
        await client.refetchQueries({
          include: ['mapMarkers'],
        });
      }
    };

    const fetchDashboardStatForFWE = async () => {
      await client.refetchQueries({
        include: [
          'commodities',
          'commodityStatsClaimedAndUnassignedFWE',
          'commodityStatsRejectedFWE',
          'commodityWeeklyStats',
          'commodityFitupStats',
          'commodityDashboardTabStats',
          'commodityWeeklyStats',
          'commodityStatsAllPriorityStats',
          'assignToMeStatsFWE',
          'commodityBlocksStatsFWE',
        ],
      });

      if (features?.showMapView) {
        await client.refetchQueries({
          include: ['fweMapMarkers', 'fweFitupMapMarkers', 'fweApprovedMapMarkers'],
        });
      }
    };
    const fetchWeldList = async () => {
      await client.refetchQueries({
        include: ['commodities'],
      });
    };

    if (!data || !data.commoditiesUpdated) return;

    //If not these four persona's Sup/Gf/Foreman/FWE Return
    if (
      !(
        includes([...corePersonaTypes, PersonaType.FIELD_WELD_ENGINEER], user?.crewMember?.persona) ||
        includes([...corePersonaTypes, PersonaType.FIELD_WELD_ENGINEER], dashboardSelectedUser?.persona)
      )
    )
      return;

    const affectedOwnerIds = data?.commoditiesUpdated?.affectedOwnerIds;

    //if the user is Foreman/Gf/Super or SuperAdmin dashboard selected user is Foreman/Gf/Super
    if (
      affectedOwnerIds &&
      (includes(affectedOwnerIds, user?.id) || includes(affectedOwnerIds, dashboardSelectedUser?.id)) &&
      (includes(corePersonaTypes, user?.crewMember?.persona) ||
        includes(corePersonaTypes, dashboardSelectedUser?.persona))
    ) {
      if (isASAppType && pathName && pathName === '/') {
        fetchDashboardStatForSuperGfForeman();
      }
      if (isASAppType && pathName && pathName.includes('/welds')) {
        fetchWeldList();
      }
    }
   //if the user is FWE or  dashboard selected user is FWE then below queries will refresh
    // here we also checked affectedOwnerId length check it help to differentiate b/w spool commodities and weld commodities because same subscription fire in both case
    //and spool not have any owner but weld commodities atleast have one owner for any activity, so now it will not relaod FWE screen if any activity only on spool from PF side
    if (
      (includes([PersonaType.FIELD_WELD_ENGINEER], user?.crewMember?.persona) ||
        includes([PersonaType.FIELD_WELD_ENGINEER], dashboardSelectedUser?.persona)) &&
      affectedOwnerIds?.length > 0
    ) {
      if (isASAppType && pathName && pathName === '/') {
        fetchDashboardStatForFWE();
      }
      if (isASAppType && pathName && pathName.includes('/welds')) {
        fetchWeldList();
      }
    }
  }, [location, data, user, dashboardSelectedUser]);

  return null;
}
